import { SettlementCategory } from './types';

/**
 * 수업 1건에 적용된 규칙(서버 `appliedRule`)을 화면 문구로 바꾼다.
 * 'custom' 수강권 개별 요율 · 'category:<key>' 분류 예외 · 'reRegistration' 재등록 예외 · 'tier' 구간표.
 *
 * `isCustomedCommission` 으로 판단하지 않는다 — 그 필드는 개별 요율만 뜻한다.
 * 배수 규칙이면 서버가 준 `exceptionMultiplier` 를 그대로 붙인다(화면에서 계산하지 않는다).
 */
export type AppliedRuleKind = 'custom' | 'category' | 'reRegistration' | 'tier' | 'unknown';

export const parseAppliedRule = (appliedRule: unknown): { kind: AppliedRuleKind; categoryKey?: string } | null => {
    if (typeof appliedRule !== 'string' || !appliedRule) return null;
    if (appliedRule === 'custom') return { kind: 'custom' };
    if (appliedRule === 'reRegistration') return { kind: 'reRegistration' };
    if (appliedRule === 'tier') return { kind: 'tier' };
    if (appliedRule.startsWith('category:')) return { kind: 'category', categoryKey: appliedRule.slice('category:'.length) };
    return { kind: 'unknown' };
};

/** 예외 규칙(분류·재등록)이 적용된 수업인가. 이 행의 수업료는 서버 값을 그대로 써야 한다. */
export const isExceptionAppliedRule = (appliedRule: unknown): boolean => {
    const parsed = parseAppliedRule(appliedRule);
    return parsed?.kind === 'category' || parsed?.kind === 'reRegistration';
};

/** 배수 표기. 0.0001 단위까지 서버 값을 그대로 보인다. */
export const formatMultiplier = (multiplier: unknown): string | null => {
    if (typeof multiplier !== 'number' || !Number.isFinite(multiplier) || multiplier <= 0) return null;
    return `×${Number(multiplier.toFixed(4))}`;
};

export const buildCategoryNameMap = (categories: SettlementCategory[] | undefined): Map<string, SettlementCategory> =>
    new Map((categories ?? []).map((category) => [category.key, category]));

/**
 * 「항상 IN 요율의 배수」(서버 exceptionMode === 'inMultiplier') 표기 — 「IN 요율」, 배수가 1 이 아니면 「IN 요율 ×1.2」.
 * 값은 서버 그대로. 배수가 없거나 이상하면 「IN 요율」만.
 */
export const formatInMultiplierRuleText = (multiplier: unknown): string => {
    const text = typeof multiplier === 'number' && multiplier !== 1 ? formatMultiplier(multiplier) : null;
    return text ? `IN 요율 ${text}` : 'IN 요율';
};

/**
 * 규칙 이름. appliedRule 이 없으면(구서버) null — 호출부는 칸을 비우거나 열을 숨긴다.
 * exceptionMode 가 'inMultiplier' 면 배수 대신 「IN 요율 ×1.2」로 붙인다(시간과 상관없이 IN 요율로 계산한 수업).
 */
export const formatAppliedRuleLabel = (
    appliedRule: unknown,
    categoryMap: Map<string, SettlementCategory>,
    exceptionMultiplier?: unknown,
    exceptionMode?: unknown,
): string | null => {
    const parsed = parseAppliedRule(appliedRule);
    if (!parsed) return null;
    let label: string;
    switch (parsed.kind) {
        case 'custom':
            label = '개별 요율';
            break;
        case 'reRegistration':
            label = '재등록';
            break;
        case 'tier':
            label = '기본';
            break;
        case 'category':
            label = categoryMap.get(parsed.categoryKey ?? '')?.name ?? '알 수 없는 분류';
            break;
        default:
            label = '-';
    }
    if (exceptionMode === 'inMultiplier' && (parsed.kind === 'category' || parsed.kind === 'reRegistration')) {
        return `${label} · ${formatInMultiplierRuleText(exceptionMultiplier)}`;
    }
    const multiplierText = formatMultiplier(exceptionMultiplier);
    return multiplierText ? `${label} ${multiplierText}` : label;
};

/**
 * 「적용 규칙」을 보일 만한 행이 있는가 — 분류·재등록 예외가 한 행이라도 있을 때만.
 * 서버는 tier 센터의 모든 PT 행에 appliedRule 을 싣는다(예외가 없으면 'tier', 개별 요율이면 'custom').
 * 그 둘뿐이면 열·소계를 그리지 않아 새 기능을 안 쓰는 센터의 화면·엑셀 모양이 바뀌지 않는다.
 * 화면 표·화면 엑셀·급여 엑셀·급여 소계가 이 하나를 같이 쓴다.
 */
export const hasExceptionRuleRows = (rows: Array<{ appliedRule?: unknown } | null | undefined> | null | undefined): boolean =>
    !!rows && rows.some((row) => isExceptionAppliedRule(row?.appliedRule));

/**
 * 기본 정산 예외 행의 보조 문구 — 서버 exceptionValueSource(어느 칸 값을 썼는지).
 * 'new'(신규 값)는 기본이라 문구를 붙이지 않는다. 모르는 값·없음이면 null.
 */
export const formatExceptionValueSource = (source: unknown): string | null => {
    if (source === 'reRegistration') return '재등록 값';
    if (source === 'noshow') return '노쇼 값';
    return null;
};

/**
 * 엑셀 정산액 칸에 서버 금액을 그대로 넣어야 하는 행인가(수식 「수업 금액 × 정산 비율」로 다시 만들면 안 되는 행).
 * 분류·재등록 예외가 적용된 행은 정산 방식과 무관하게 서버 값이다 — 배수 행은 정산 비율이 원래 요율이라 수식이 배수를 빠뜨리고,
 * 기본 정산의 요율(%) 예외 출석 행은 서버가 반올림해 수식(내림)과 1원 다를 수 있다.
 */
export const usesServerScheduleFeeInExcel = (row: { appliedRule?: unknown } | null | undefined): boolean =>
    isExceptionAppliedRule(row?.appliedRule);

/** 엑셀 「적용 규칙」 칸: 규칙 이름 뒤에 「· 재등록 값」·「· 노쇼 값」을 붙인다(예외 행에서만). */
export const withExceptionValueSource = (
    label: string,
    row: { appliedRule?: unknown; exceptionValueSource?: unknown } | null | undefined,
): string => {
    if (!isExceptionAppliedRule(row?.appliedRule)) return label;
    const text = formatExceptionValueSource(row?.exceptionValueSource);
    return text ? `${label} · ${text}` : label;
};
